import { useCallback, useEffect, useState } from "react";
import { claimsApi } from "../api/claimsApi";
import { parseOfficeCodes } from "../claimsOfficeUtils";
import { claimStatusToApi, claimTypeToApi, mapSummaryDto } from "../claimsMappers";
import { DEFAULT_CLAIM_SORT_BY } from "../TabContent/claimsTableConfig";

export const useClaimsListData = ({
  officeCode,
  statusFilter,
  typeFilter,
  searchQuery = "",
  page = 1,
  size = 50,
  sortBy = DEFAULT_CLAIM_SORT_BY,
  ascending = false,
  refreshKey = 0,
  onPageOutOfRange,
}) => {
  const [claims, setClaims] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (!isLoading) {
      setShowLoadingOverlay(false);
      return;
    }
    const t = setTimeout(() => setShowLoadingOverlay(true), 250);
    return () => clearTimeout(t);
  }, [isLoading]);

  const fetchClaims = useCallback(
    async (signal) => {
      if (!officeCode) {
        setClaims([]);
        setTotalElements(0);
        setTotalPages(1);
        return;
      }

      try {
        setIsLoading(true);
        setErrorMessage("");

        const searchRequest = {
          query: searchQuery?.trim() || null,
          sortBy: sortBy || DEFAULT_CLAIM_SORT_BY,
          ascending: Boolean(ascending),
        };

        if (statusFilter && statusFilter !== "All Statuses") {
          searchRequest.status = claimStatusToApi(statusFilter);
        }
        if (typeFilter && typeFilter !== "All Claim Types") {
          searchRequest.claimType = claimTypeToApi(typeFilter);
        }

        const response = await claimsApi.searchClaims(searchRequest, {
          page: Math.max(0, page - 1),
          size,
          officeCode: officeCode || undefined,
          signal,
        });

        const content = (response?.content || []).map((dto) =>
          mapSummaryDto(dto, dto.officeCode || parseOfficeCodes(officeCode)[0])
        );
        const fetchedTotalPages = Math.max(1, response?.totalPages ?? 1);
        setClaims(content);
        setTotalElements(response?.totalElements ?? content.length);
        setTotalPages(fetchedTotalPages);

        if (page > 1 && page > fetchedTotalPages) {
          onPageOutOfRange?.(1);
        }
      } catch (error) {
        if (error?.name === "CanceledError" || error?.code === "ERR_CANCELED") {
          return;
        }
        setClaims([]);
        setTotalElements(0);
        setTotalPages(1);
        setErrorMessage(error?.response?.data?.message || "Failed to load claims.");
      } finally {
        setIsLoading(false);
      }
    },
    [
      officeCode,
      page,
      searchQuery,
      size,
      sortBy,
      ascending,
      statusFilter,
      typeFilter,
      onPageOutOfRange,
    ]
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchClaims(controller.signal);
    return () => controller.abort();
  }, [fetchClaims, refreshKey]);

  return {
    claims,
    totalElements,
    totalPages,
    isLoading,
    showLoadingOverlay,
    errorMessage,
    refetch: fetchClaims,
  };
};

export const useClaimsSummary = ({ officeCode, refreshKey = 0 }) => {
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!officeCode) {
      setSummary(null);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    claimsApi
      .getSummary(officeCode)
      .then((data) => {
        if (!cancelled) setSummary(data);
      })
      .catch(() => {
        if (!cancelled) setSummary(null);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [officeCode, refreshKey]);

  return { summary, isLoading };
};
